import { alertClasses, paperClasses } from '@mui/material';
import {
  BulkSettlement_Create,
  BulkSettlementCreateFor,
  InvoiceCategoryFilter,
  postV1CompaniesCompanyIdSettlementsBulkCreate,
  UserBulkCreateSettlementChannel_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { PreparingSettlementsToaster } from '~components/Toasters/PreparingSettlementsToaster';
import { SettlementsCreatedToaster } from '~components/Toasters/SettlementsCreatedToaster';
import {
  SettlementDTO,
  SettlementFormFields,
  SettlementFormFieldsHandler,
} from '~formsShared/components/SettlementFormFields';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { secondsSince } from '~hooks/useAnalytics/useSecondsOnPageTracker';
import {
  BulkSettlementCreateResult,
  deparseSettlementCreateTerms,
  extractGroupingsFromSettlementDTO,
} from '~hooks/useSettlements/models';
import { TabQSParam } from '~pages/Approvals/DriverPay/TabQSParam';
import { ROUTES } from '~router/routePaths';
import connection from '~services/connectionModule';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DriverPayPageContext from '../../DriverPayPageContext';
import {
  BulkCreateSettlementFormFields,
  BulkFormFieldsHandler,
  BulkSettlementDTO,
} from './BulkCreateSettlementFormFields';

function buildBulkSettlementBody(bulkFormData: BulkSettlementDTO, data: SettlementDTO) {
  const customerIds = (bulkFormData.customerAccountIds ?? [])
    .map((item) => (item as { value: string })?.value)
    .filter(Boolean);
  const vendorIds = (bulkFormData.vendorAccountIds ?? [])
    .map((item) => (item as { value: string })?.value)
    .filter(Boolean);
  const driverIds = (bulkFormData.driverIds ?? [])
    .map((item) => (item as { value: string })?.value)
    .filter(Boolean);

  // Entity types without specific IDs go into bulk_create_for
  const bulkCreateFor = bulkFormData.entityType
    .map((entity) => entity.value)
    .filter((entityType) => {
      if (entityType === BulkSettlementCreateFor.CUSTOMERS && customerIds.length > 0) {
        return false;
      }
      if (entityType === BulkSettlementCreateFor.VENDORS && vendorIds.length > 0) {
        return false;
      }
      if (entityType === BulkSettlementCreateFor.DRIVERS && driverIds.length > 0) {
        return false;
      }
      return true;
    });

  // The picker returns days at midnight; the backend filters an inclusive timestamp
  // range, so expand to full-day boundaries in the company timezone (matching
  // DriverPayQueryContext) or a single-day selection collapses to one instant.
  const startDate = bulkFormData.dateRange[0];
  const endDate = bulkFormData.dateRange[1];

  const body: BulkSettlement_Create = {
    start_date: startDate ? dayjs.tz(startDate).startOf('day').toISOString() : '',
    end_date: endDate ? dayjs.tz(endDate).endOf('day').toISOString() : '',
    bulk_create_for: bulkCreateFor,
    settlement_config: {
      settlement_line_item_type: data.settlementLineItemType,
      settlement_line_item_groupings: extractGroupingsFromSettlementDTO(data) ?? [],
      ...deparseSettlementCreateTerms(data),
    },
  };

  if (customerIds.length > 0) {
    body.customer_account_ids = customerIds;
  }
  if (vendorIds.length > 0) {
    body.vendor_account_ids = vendorIds;
  }
  if (driverIds.length > 0) {
    body.driver_ids = driverIds;
  }

  return body;
}

type EntityTypeOption = { value: BulkSettlementCreateFor; label: string };

const BULK_SETTLEMENT_PREPARING_TIMEOUT_MS = 60_000;

// Shared by every CTA instance rather than held per instance: a run reuses a
// still-open channel subscription, so its result can reach the handler of the
// instance that opened it, and that handler must clear this run's timeout.
let safetyTimeoutId: number | null = null;

let settlementBuild = { source: '', startedAt: 0 };
let submittedSettlementBuild = settlementBuild;

function trackSettlementBuildStarted(source: string) {
  settlementBuild = { source, startedAt: Date.now() };
  AnalyticsController.track('settlement_build_started', { source });
}

function clearSafetyTimeout() {
  if (safetyTimeoutId !== null) {
    window.clearTimeout(safetyTimeoutId);
    safetyTimeoutId = null;
  }
}

interface BulkCreateSettlementCTAProps {
  disabled?: boolean;
  category?: InvoiceCategoryFilter;
  initialEntityTypes?: EntityTypeOption[];
  entityTypeLocked?: boolean;
  initialDateRange?: [Dayjs, Dayjs];
  autoOpen?: boolean;
  onDialogClose?: () => void;
  onBulkCreateFinished?: () => void;
}

const BulkCreateSettlementCTA = observer(
  ({
    disabled = false,
    category: categoryProp,
    initialEntityTypes,
    entityTypeLocked,
    initialDateRange,
    autoOpen = false,
    onDialogClose,
    onBulkCreateFinished,
  }: BulkCreateSettlementCTAProps) => {
    const navigate = useNavigate();
    const { userStore, toasterStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);

    const driverPayPageContext = useContext(DriverPayPageContext);
    const category = categoryProp ?? driverPayPageContext.currentCategory;

    const [isDialogOpen, setIsDialogOpen] = useState(autoOpen);
    const [isLoading, setIsLoading] = useState(false);
    const [isWaitingForPaymentTerms, setIsWaitingForPaymentTerms] = useState(false);

    const formRef = useRef<SettlementFormFieldsHandler>(null);
    const bulkFormRef = useRef<BulkFormFieldsHandler>(null);

    useEffect(() => {
      if (autoOpen) {
        trackSettlementBuildStarted('approve_jobs_menu');
        setIsDialogOpen(true);
      }
    }, [autoOpen]);

    const handleOpenDialog = () => {
      trackSettlementBuildStarted('driver_pay_bulk_cta');
      setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
      setIsDialogOpen(false);
      onDialogClose?.();
    };

    const subscribeToBulkSettlementChannel = useCallback(
      (userId: string) => {
        const consumer = connection.realTimeConnection;
        let subscription = consumer?.subscriptions.subscriptions.find((sub) => {
          return (
            sub.identifier.includes(realTimeChannels.UserBulkCreateSettlementChannel) &&
            sub.identifier.includes(userId)
          );
        });

        if (!subscription) {
          subscription = consumer?.subscriptions?.create?.(
            {
              channel: realTimeChannels.UserBulkCreateSettlementChannel,
              id: userId,
            },
            {
              received(data: UserBulkCreateSettlementChannel_Read) {
                clearSafetyTimeout();
                toasterStore.hidePermanentAlert();

                const bulkSettlementCreateResult = BulkSettlementCreateResult.parse(data);
                onBulkCreateFinished?.();

                const settlementCount =
                  bulkSettlementCreateResult.createdCustomerSettlements.count +
                  bulkSettlementCreateResult.createdVendorSettlements.count +
                  bulkSettlementCreateResult.createdDriverSettlements.count;

                if (settlementCount > 0) {
                  AnalyticsController.track('settlement_created', {
                    seconds_from_start: secondsSince(submittedSettlementBuild.startedAt),
                    settlement_count: settlementCount,
                    source: submittedSettlementBuild.source,
                  });
                }

                toasterStore.push(
                  alert(
                    <SettlementsCreatedToaster
                      onClose={() => {
                        toasterStore.hidePermanentAlert();
                      }}
                      onViewSettlements={() => {
                        // Payables and Receivables share this route, split only by the
                        // category param — omitting it makes NavigationTabs redirect to
                        // receivables and drop the tab param with it.
                        navigate(
                          `/${ROUTES.approvals.base}/${ROUTES.approvals.driverPay}?category=${category}&tab=${TabQSParam.SETTLEMENTS}`,
                        );
                      }}
                      bulkSettlementCreateResult={bulkSettlementCreateResult}
                    />,
                    AlertTypes.success,
                    undefined,
                    undefined,
                    true,
                    {
                      [`& .${alertClasses.icon}`]: { display: 'none' },
                      [`& .${alertClasses.message}`]: { p: 0 },
                    },
                    { vertical: 'bottom', horizontal: 'right' },
                    true,
                  ),
                  true,
                );

                this.unsubscribe();
              },
            },
          );
        }

        return subscription;
      },
      [category, navigate, onBulkCreateFinished, toasterStore],
    );

    const handleConfirm = async () => {
      // Submit both forms and combine their results
      if (bulkFormRef.current?.onSubmit && formRef.current?.onSubmit) {
        try {
          const bulkFormData = await bulkFormRef.current.onSubmit();

          formRef?.current?.onSubmit(async (data: SettlementDTO) => {
            setIsLoading(true);
            const body = buildBulkSettlementBody(bulkFormData, data);

            // Subscribe BEFORE the API call: the backend can broadcast its
            // result before a post-call subscription is established, especially
            // when the job has nothing to do (e.g. 0 invoices match).
            const subscription = subscribeToBulkSettlementChannel(
              userStore.user?.id ?? '',
            );

            // Show preparing toast
            toasterStore.push(
              alert(
                <PreparingSettlementsToaster
                  onClose={() => {
                    clearSafetyTimeout();
                    subscription?.unsubscribe?.();
                    toasterStore.hidePermanentAlert();
                  }}
                />,
                'info',
                undefined,
                undefined,
                true,
                {
                  [`& .${paperClasses.root}`]: {
                    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
                    maxWidth: '250px',
                    overflow: 'hidden',
                    p: 0,
                  },
                  [`& .${alertClasses.icon}`]: { display: 'none' },
                  [`& .${alertClasses.message}`]: { p: 0 },
                },
                { vertical: 'bottom', horizontal: 'right' },
                true,
              ),
              true,
            );
            const preparingToast = toasterStore.permanentAlert;

            // Safety net for dropped ActionCable messages: if no broadcast
            // arrives, dismiss the spinner and surface a fallback message so
            // the user is never permanently stuck. It deliberately outlives this
            // component: the Approve Jobs menu unmounts the CTA as soon as the
            // dialog closes, while the spinner stays on screen.
            clearSafetyTimeout();
            submittedSettlementBuild = settlementBuild;
            safetyTimeoutId = window.setTimeout(() => {
              safetyTimeoutId = null;
              subscription?.unsubscribe?.();

              // The toaster holds a single permanent alert, so only clear the
              // spinner if another alert has not replaced it in the meantime.
              const preparingToastIsStillShowing =
                toasterStore.permanentAlert === preparingToast;
              if (preparingToastIsStillShowing) {
                toasterStore.hidePermanentAlert();
              }

              toasterStore.push(
                alert(
                  t(
                    'approvals.driver_pay.settlements.bulk_create_settlement.taking_longer_than_expected',
                  ),
                  AlertTypes.info,
                ),
              );
            }, BULK_SETTLEMENT_PREPARING_TIMEOUT_MS);

            try {
              await postV1CompaniesCompanyIdSettlementsBulkCreate({
                path: { 'company-id': companyId },
                body,
              });
            } catch (error) {
              clearSafetyTimeout();
              subscription?.unsubscribe?.();
              toasterStore.hidePermanentAlert();
              connection.handleRequestError(
                error,
                t('error_messages.settlements.failed_to_create'),
                [422],
              );
            } finally {
              setIsLoading(false);
            }

            handleCloseDialog();
          });
        } catch (error) {
          console.error('Error submitting bulk form', error);
        }
      }
    };

    return (
      <>
        {!autoOpen && (
          <SmallButton
            color="brandV2Black"
            disabled={disabled}
            onClick={handleOpenDialog}
            variant="contained"
          >
            {t('approvals.driver_pay.bulk_create_settlement')}
          </SmallButton>
        )}

        <Dialog isOpen={isDialogOpen} onClose={handleCloseDialog}>
          <DialogHeader onClose={handleCloseDialog}>
            {t('approvals.driver_pay.bulk_create_settlement')}
          </DialogHeader>

          <DialogContent>
            <BulkCreateSettlementFormFields
              ref={bulkFormRef}
              category={category}
              companyId={companyId}
              initialEntityTypes={initialEntityTypes}
              entityTypeLocked={entityTypeLocked}
              initialDateRange={initialDateRange}
            />
            <SettlementFormFields
              ref={formRef}
              allowEmptyInvoiceIds
              onPaymentTermsLoadingChange={setIsWaitingForPaymentTerms}
            />
          </DialogContent>

          <DialogActions>
            <SmallButton color="secondary" onClick={handleCloseDialog} variant="outlined">
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              color="primary"
              disabled={isWaitingForPaymentTerms}
              loading={isLoading}
              onClick={handleConfirm}
              variant="contained"
            >
              {t('actions.confirm')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

export default BulkCreateSettlementCTA;
